/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { FieldBase, useAppForm } from '@/components/form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTrigger,
} from '@e2n/ui';
import { useSettings } from '@/domain/dienstplan/features/SettingsMindestbelegung/hooks/useSettings';
import { Arbeitsbereich } from '@/domain/dienstplan/shared/types/Arbeitsbereich';
import { Weekday } from '@/domain/dienstplan/shared/types/Weekday';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { mapWochentagForAPI } from '@/domain/dienstplan/shared/utils/mapWochentagForAPI';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getHoursAndMinutesFromMinutes } from '@/utils';
import { transformTimeStringToMinutes } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/transformTimeStringToMinutes';

type SettingsMindestbelegungProps = {
  wochentag: Weekday;
  arbeitsbereich: Arbeitsbereich;
  hasBelegungen: boolean;
};

export function SettingsMindestbelegung({
  wochentag,
  arbeitsbereich,
  hasBelegungen,
}: SettingsMindestbelegungProps) {
  const { t } = useTranslation();
  const [updateSettings] = useSettings();
  const { manager } = useProfile();
  const [showDialog, setShowDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const einstellungen = arbeitsbereich.mindestbelegungen.find(
    (w) => w.tag === wochentag,
  )?.einstellungen;

  const formSchema = z
    .object({
      maxMitarbeiteranzahl: z.string().regex(/^\d+$/, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors.maxMitarbeiterAnzahl',
        ),
      }),
      minimaleSchichtlaenge: z.string().min(1, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors.minSchichtlaengeNotEmpty',
        ),
      }),
      maximaleSchichtlaenge: z.string().min(1, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors.maxSchichtlaengeNotEmpty',
        ),
      }),
      unbesetzteSchichten: z.enum(['yes', 'no']),
      schichtplanung: z.enum(['yes', 'no']),
      bemerkung: z.string(),
    })
    .superRefine((data, ctx) => {
      const value = parseInt(data.maxMitarbeiteranzahl);
      if (value <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.einstellungen.validationErrors.maxMitarbeiterAnzahl',
          ),
          path: ['maxMitarbeiteranzahl'],
        });
      }

      if (!data.minimaleSchichtlaenge || !data.maximaleSchichtlaenge) {
        return;
      }

      const minimaleSchichtlaenge = transformTimeStringToMinutes(
        data.minimaleSchichtlaenge,
      );
      const maximaleSchichtlaenge = transformTimeStringToMinutes(
        data.maximaleSchichtlaenge,
      );

      if (minimaleSchichtlaenge > maximaleSchichtlaenge) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.einstellungen.validationErrors.schichtlaenge',
          ),
          path: ['minimaleSchichtlaenge'],
        });
      }
    });

  const defaultMinimaleSchichtlaenge = `${getHoursAndMinutesFromMinutes(
    einstellungen?.minimaleSchichtlaenge ?? 0,
  )
    .hours.toString()
    .padStart(2, '0')}:${getHoursAndMinutesFromMinutes(
    einstellungen?.minimaleSchichtlaenge ?? 0,
  )
    .minutes.toString()
    .padStart(2, '0')}`;

  const defaultMaximaleSchichtlaenge = `${getHoursAndMinutesFromMinutes(
    einstellungen?.maximaleSchichtlaenge ?? 0,
  )
    .hours.toString()
    .padStart(2, '0')}:${getHoursAndMinutesFromMinutes(
    einstellungen?.maximaleSchichtlaenge ?? 0,
  )
    .minutes.toString()
    .padStart(2, '0')}`;

  const form = useAppForm({
    defaultValues: {
      maxMitarbeiteranzahl: einstellungen?.maxAnzahlMitarbeiter.toString(),
      unbesetzteSchichten: einstellungen?.unbesetzteSchichten ? 'yes' : 'no',
      schichtplanung: hasBelegungen
        ? 'yes'
        : einstellungen?.schichtplanung
          ? 'yes'
          : 'no',
      bemerkung: einstellungen?.bemerkung,
      minimaleSchichtlaenge: defaultMinimaleSchichtlaenge,
      maximaleSchichtlaenge: defaultMaximaleSchichtlaenge,
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const managerId = manager?.id;
      if (!managerId) {
        toast({
          title: 'Mindestbelegung',
          description: 'Profil konnte nicht geladen werden',
        });
        return;
      }
      const result = await updateSettings({
        input: {
          bemerkung: value.bemerkung ?? '',
          idArbeitsbereich: arbeitsbereich.id,
          managerId: managerId,
          maxMitarbeiterAnzahl: value.maxMitarbeiteranzahl
            ? parseInt(value.maxMitarbeiteranzahl)
            : 0,
          tag: mapWochentagForAPI(wochentag),
          unbesetzteSchichtenErlauben: value.unbesetzteSchichten === 'yes',
          minimaleSchichtlaenge: transformTimeStringToMinutes(
            value.minimaleSchichtlaenge,
          ),
          maximaleSchichtlaenge: transformTimeStringToMinutes(
            value.maximaleSchichtlaenge,
          ),
          schichtplanungBeruecksichtigen: value.schichtplanung === 'yes',
        },
      });

      if (
        result.data?.dienstplan_update_einstellungen?.__typename ===
        'dienstplan_einstellungen'
      ) {
        toast({
          title: 'Mindestbelegung',
          description: 'Einstellungen erfolgreich gespeichert',
        });
        setIsOpen(false);
      }
      formApi.reset();
    },
  });

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (open === false && form.state.isDirty) {
            setShowDialog(true);
            return;
          }
          setIsOpen(open);
        }}>
        <SheetTrigger asChild>
          <Button variant="icon">
            <FontAwesomeIcon icon={faGear} />
          </Button>
        </SheetTrigger>
        <SheetContent className="flex flex-col gap-6">
          <SheetHeader className="flex flex-col space-y-1">
            <h1 className="text-neutrals-700 text-sm">
              {getLocaleWeekdayString(wochentag)}
            </h1>
            <DialogTitle className="text-neutrals-900 text-xl font-bold">
              {arbeitsbereich.name}
            </DialogTitle>
            <SheetDescription className="hidden">
              {arbeitsbereich.name}
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4">
            <div className="flex flex-col gap-6">
              <form.AppField name="maxMitarbeiteranzahl">
                {(field) => (
                  <field.TextInput
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.mitarbeiterMax',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="unbesetzteSchichten">
                {(field) => (
                  <FieldBase
                    infoText={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.schichtenTooltip',
                    )}
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.schichten',
                    )}>
                    <RadioGroup
                      className="flex gap-4"
                      onValueChange={field.handleChange}
                      value={field.state.value}>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="yes" value="yes" />
                        <Label htmlFor="yes">{t('actions.yes')}</Label>
                      </div>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="no" value="no" />
                        <Label htmlFor="no">{t('actions.no')}</Label>
                      </div>
                    </RadioGroup>
                  </FieldBase>
                )}
              </form.AppField>
              <form.AppField name="minimaleSchichtlaenge">
                {(field) => (
                  <field.TimeInput
                    isRequired
                    suffix={t('general.date.hours')}
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.minimaleSchichtlaenge',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="maximaleSchichtlaenge">
                {(field) => (
                  <field.TimeInput
                    isRequired
                    suffix={t('general.date.hours')}
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.maximaleSchichtlaenge',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="schichtplanung">
                {(field) => (
                  <FieldBase
                    infoText={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.inSchichtplanungBeruecksichtigenTooltip',
                    )}
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.schichtplanung',
                    )}>
                    <RadioGroup
                      disabled={hasBelegungen}
                      className="flex gap-4"
                      onValueChange={field.handleChange}
                      value={field.state.value}>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="yes" value="yes" />
                        <Label htmlFor="yes">{t('actions.yes')}</Label>
                      </div>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="no" value="no" />
                        <Label htmlFor="no">{t('actions.no')}</Label>
                      </div>
                    </RadioGroup>
                  </FieldBase>
                )}
              </form.AppField>
              <form.AppField name="bemerkung">
                {(field) => (
                  <field.TextArea
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.bemerkung',
                    )}
                    maxLength={250}
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex w-full justify-end">
              <form.Subscribe
                selector={(state) => ({
                  isSubmitting: state.isSubmitting,
                  canSubmit: state.canSubmit,
                })}>
                {({ isSubmitting, canSubmit }) => (
                  <Button disabled={!canSubmit || isSubmitting} type="submit">
                    {t(
                      'domain.dienstplan.mindestbelegung.einstellungen.speichern',
                    )}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </SheetContent>
      </Sheet>
      {showDialog && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogPortal>
            <DialogContent className="z-55">
              <DialogTitle>
                {t(
                  'domain.dienstplan.mindestbelegung.einstellungen.dialog.title',
                )}
              </DialogTitle>
              <DialogDescription>
                <div className="flex flex-col gap-8 w-full">
                  <p>
                    {t(
                      'domain.dienstplan.mindestbelegung.einstellungen.dialog.despription',
                    )}
                  </p>
                  <div className="flex justify-end w-full gap-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        form.reset();
                        setShowDialog(false);
                        setIsOpen(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.verlassen',
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDialog(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.bearbeiten',
                      )}
                    </Button>
                  </div>
                </div>
              </DialogDescription>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  );
}
